<template>
  <div
    id="lsDemoRule"
    data-testid="lsDemoRule">
    <div
      v-if="segments && segments.length > 0"
      class="content mb-4">
      <div
        id="gender"
        class="section">
        <div class="separator">
          <div>
            <icon-colored-demographic />
            <p>{{ capitalize($t('names.gender', { n: 1 })) }}</p>
          </div>
        </div>

        <div class="content">
          <div
            v-if="warnings.length > 0"
            class="warning alert alert-warning">
            <div class="title">
              <icon-warning />
              <span>{{ capitalize($t('messages.followingDemographicsAreDisabled')) }}:</span>
            </div>
            <ul>
              <li
                v-for="warning in warnings"
                :key="warning">
                {{ warning }}
              </li>
            </ul>
          </div>

          <selectable-tiles
            :items="genderOptions"
            :should-display-labels="true"
            :should-display-icons="true"
            :should-display-checkmark-on-selected-items="true"
            :is-multiselect="false"
            :justify-content-left="true"
            :selected-items="[selectedGender]"
            :allow-empty="false"
            label-property-name="name"
            icon-property-name="icon"
            tile-margin-bottom="1.6rem"
            tile-margin-right="1.2rem"
            tile-padding="1rem"
            icon-size="12rem"
            @selection-changed="genderSelected" />
        </div>
      </div>

      <div
        id="age"
        class="section">
        <div class="separator">
          <div>
            <icon-colored-teenager />
            <p>{{ capitalize($t('names.age', { n: 1 })) }}</p>
          </div>
        </div>

        <div class="content">
          <div class="slider-container">
            <icon-colored-teenager />
            <slider
              v-if="age !== null"
              v-model="age"
              :min="0"
              :min-range="1"
              :contained="true"
              :max="ageRanges.length - 1"
              :max-range="ageRanges.length - 1"
              :marks="ageMarks"
              :enable-cross="true"
              :tooltip-formatter="(val) => ageRanges[val]"
              :step-style="{ display: 'none' }">
              <template #label="{ label, active }">
                <div :class="['vue-slider-mark-label', 'custom-label', { active }]">
                  <span>{{ ageRanges[label] }}</span>
                </div>
              </template>
            </slider>
          </div>
        </div>
      </div>

      <div
        id="countries"
        class="section">
        <div class="separator">
          <div>
            <icon-colored-globe />
            <p>{{ capitalize($t('names.country', { n: 2 })) }}</p>
          </div>
        </div>

        <div class="content">
          <multiselect
            v-model="selectedCountries"
            :label="isWindowsOS() ? 'name' : 'formatted'"
            track-by="code"
            :multiple="true"
            :close-on-select="false"
            :clear-on-select="false"
            :preserve-search="true"
            :options="countries"
            @input="countriesSelected">
            <template #option="{ option }">
              {{ isWindowsOS() ? option.name : option.formatted }}
            </template>
          </multiselect>
        </div>
      </div>
    </div>
    <div
      v-else
      class="uh-oh">
      <p class="alert-warning">
        <icon-colored-sad />
        {{ $t('messages.noDemographicsAvailable') }}
      </p>
    </div>
  </div>
</template>

<script>
  import 'vue-slider-component/theme/antd.css';

  import { capitalize } from 'vue';
  import Slider from 'vue-slider-component';
  import { mapActions } from 'vuex';

  import { SelectableTiles } from '@/common/components/controls';
  import { Ages, Genders } from '@/common/components/targeting';
  import { AudienceRuleModes } from '@/common/enums/audience-rule-modes';
  import { isLifesightDataProvider } from '@/common/enums/data-provider-codes';
  import DemographicCategory from '@/common/enums/DemographicCategory.enum';
  import {
    IconColoredDemographic,
    IconColoredGlobe,
    IconColoredSad,
    IconColoredTeenager,
  } from '@/common/icons/colored';
  import { IconWarning } from '@/common/icons/monochrome';
  import { Filter, FilterCollection, FilterComparisonOperator, QueryParams } from '@/common/models';
  import LifesightAgeDemographic from '@/common/models/demographics/LifesightAgeDemographic';
  import { first, formatNumber, isWindowsOS, last, orderBy, uniq, uniqBy } from '@/globals';
  import AudienceSegmentUtils from '@/utils/AudienceSegmentUtils';

  export default {
    components: {
      IconColoredDemographic,
      IconColoredTeenager,
      IconColoredGlobe,
      IconColoredSad,
      IconWarning,
      SelectableTiles,
      Slider,
    },
    emits: ['onLifesightDemographicSelected'],
    data() {
      return {
        age: null,
        ageDemos: [],
        ageRanges: [],
        audienceSegments: [],
        name: null,
        segments: null,
        selectedAgeRange: null,
        selectedCountries: [],
        selectedGender: null,
        targetedSegments: [],
        timeout: null,
      };
    },
    computed: {
      AudienceRuleModes() {
        return AudienceRuleModes;
      },
      ageMarks() {
        return this.ageRanges.map((x, i) => i);
      },
      countries() {
        if (!this.segments?.length) return [];
        const unique = [...new Set(this.segments.map((s) => s.location.code))];
        const list = unique.map((u) => {
          const { location } = this.segments.find((s) => s.location.code === u);
          return {
            ...location,
            formatted: `${AudienceSegmentUtils.getCountryFlag(location.code)} ${location.name}`,
          };
        });
        return orderBy(list, 'name');
      },
      demographicsFilter() {
        return new Filter('external_id', ['LSD'], FilterComparisonOperator.LIKE);
      },
      genderOptions() {
        return Genders.options();
      },
      formattedAgeRange() {
        return [...this.ageDemos].slice(this.selectedAgeRange[0], this.selectedAgeRange[1]);
      },
      warnings() {
        if (this.audienceRule.segments == null) return [];
        return this.audienceRule.segments.filter((segment) => !segment.enabled).map((segment) => segment.description);
      },
    },
    methods: {
      ...mapActions(['updateAudienceRuleName', 'updateAudienceRuleSegments']),
      ...mapActions('orgApp', ['getLifesightAudienceSegmentsByOrg', 'getAllDataProviders']),
      capitalize,
      formatNumber,
      isWindowsOS,
      ageSelected(options) {
        this.selectedAgeRange = [...options];
        this.findLifesightSegments();
      },
      countriesSelected(options) {
        this.selectedCountries = orderBy(options, 'name');
        this.findLifesightSegments();
      },
      async fetchLifesightSegments() {
        const dataProviders = await this.getAllDataProviders();
        const queryParams = new QueryParams(
          1750,
          0,
          new FilterCollection([this.demographicsFilter]),
          'reach desc',
          true,
          ['location'],
        );
        queryParams.customQueryParams.dataProviderId = dataProviders.find(isLifesightDataProvider).id;
        return queryParams.run(this.getLifesightAudienceSegmentsByOrg);
      },
      findLifesightSegments() {
        const selectedAgeRange = this.formattedAgeRange;
        const countriesSegments = this.segments.filter(
          (segment) => this.selectedCountries.map((c) => c.code).indexOf(segment.location.code) > -1,
        );
        const genderSegments =
          this.selectedGender.id === 'A'
            ? []
            : countriesSegments.filter(
                (segment) =>
                  segment.category === DemographicCategory.Gender && segment.name === this.selectedGender.name,
              );
        const ageSegments =
          selectedAgeRange.length === this.ageDemos.length
            ? []
            : countriesSegments.filter(
                (segment) =>
                  segment.category === DemographicCategory.Age && selectedAgeRange.indexOf(segment.name) > -1,
              );

        this.targetedSegments =
          genderSegments.length || ageSegments.length
            ? [...genderSegments, ...ageSegments].map((s) => ({
                ...s,
                country: s.location.name,
              }))
            : [];

        this.audienceSegments = [
          ...(genderSegments.length
            ? genderSegments
            : [...countriesSegments].filter((s) => s.category === DemographicCategory.Gender)),
          ...(ageSegments.length
            ? ageSegments
            : [...countriesSegments].filter((s) => s.category === DemographicCategory.Age)),
        ];

        this.$emit('onLifesightDemographicSelected', { segments: this.targetedSegments });
        this.updateAudienceRuleSegments({
          audienceRule: this.audienceRule,
          value: this.targetedSegments,
        });
        this.name = AudienceSegmentUtils.getDemographicsName(
          selectedAgeRange?.length === this.ageDemos?.length ? null : selectedAgeRange,
          this.selectedGender?.id === 'A' ? null : this.selectedGender,
          this.selectedCountries,
        );
      },
      genderSelected(options) {
        const option = options[0];
        this.selectedGender = option;
        this.findLifesightSegments();
      },
    },
    watch: {
      age() {
        this.ageSelected(this.age);
      },
      name(newName) {
        if (this.audienceRule) {
          this.updateAudienceRuleName({
            audienceRule: this.audienceRule,
            value: newName,
          });
        }
      },
      selectedCountries() {
        this.countriesSelected(this.selectedCountries);
      },
    },
    async mounted() {
      // get segments
      const segments = await this.fetchLifesightSegments();

      this.segments = segments.items;
      if (segments?.items?.length) {
        const ages = segments.items
          .filter((i) => i.category === DemographicCategory.Age)
          .map((i) => LifesightAgeDemographic.fromLifesightSegment(i))
          .sort((a, b) => {
            if (a.name < b.name) return -1;
            if (a.name > b.name) return 1;
            return 0;
          });

        // get age range
        this.ageRanges = Ages.options(ages);
        this.ageDemos = uniq(ages.map((a) => a.name));

        if (this.audienceRule?.segments?.length) {
          const selectedSegmentIds = this.audienceRule.segments.map((seg) =>
            AudienceSegmentUtils.getSegmentIdFromS3Path(seg.s3Path),
          );
          const selectedSegments = this.segments.filter((seg) => selectedSegmentIds.indexOf(seg.externalId) > -1);
          const selectedGender = uniq(
            selectedSegments.filter((seg) => seg.category === DemographicCategory.Gender).map((seg) => seg.name),
          )[0];
          const selectedAge = orderBy(
            uniq(selectedSegments.filter((seg) => seg.category === DemographicCategory.Age).map((seg) => seg.name)),
          );
          const selectedAgeDemosFirstIndex = this.ageDemos.findIndex((age) => age === first(selectedAge));
          const selectedAgeDemosLastIndex = this.ageDemos.findIndex((age) => age === last(selectedAge));

          this.audienceSegments = selectedSegments;
          this.selectedCountries = orderBy(
            uniqBy(
              selectedSegments.map((seg) => ({
                ...seg.location,
                formatted: `${AudienceSegmentUtils.getCountryFlag(seg.location.code)} ${seg.location.name}`,
              })),
              'code',
            ),
            'name',
          );
          this.selectedGender =
            this.genderOptions.find((gender) => gender.name === selectedGender) ||
            this.genderOptions.find((gender) => gender.id === 'A');
          this.age = selectedAge?.length
            ? [selectedAgeDemosFirstIndex, selectedAgeDemosLastIndex + 1]
            : [0, this.ageRanges.length - 1];
          this.selectedAgeRange = [...this.age];
          this.name = this.audienceRule.name;
          this.findLifesightSegments();
        } else {
          this.audienceSegments = this.segments;
          this.age = [0, this.ageRanges.length - 1];
          this.selectedGender = this.genderOptions.find((gender) => gender.id === 'A');
          this.selectedAgeRange = [...this.age];
          this.selectedCountries = orderBy([...this.countries], 'name');
          this.name = AudienceSegmentUtils.getDemographicsName(null, null, this.selectedCountries);
        }
      }
    },
    name: 'LifesightDemographicsRule',
    props: {
      audienceRule: {
        type: Object,
        default: null,
      },
      isEditing: {
        type: Boolean,
        default: false,
      },
    },
  };
</script>

<style lang="scss" scoped>
  @use '@/styles/variables' as *;

  #lsDemoRule {
    display: flex;
    flex-direction: column;

    .input {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: flex-start;
    }

    .section {
      display: flex;
      flex-direction: column;

      .separator {
        display: flex;
        align-items: center;
        justify-content: center;
        margin-top: 2rem;

        div {
          display: flex;
          align-items: center;
          justify-content: center;
          background: white;
          padding: 0 1rem;
          z-index: 1;

          svg {
            height: 2rem;
            margin-right: 1rem;
          }

          p {
            margin-bottom: 0;
            color: $lift-blue;
            text-transform: uppercase;
          }
        }
      }

      .content {
        padding-top: 2rem;
        border-top: 1px solid $borders-dark;
        margin-top: -1rem;

        .warning {
          .title {
            display: flex;
            margin-bottom: 1rem;
            align-items: center;

            svg {
              width: 2.4rem;
              height: 2.4rem;
              margin-right: 0.5rem;
            }
          }

          ul {
            margin-left: 2.8rem;
          }
        }

        .slider-container {
          display: flex;
          flex-direction: row;
          align-items: center;

          svg {
            height: 4.8rem;
            width: 4.8rem;

            &:first-of-type {
              margin-right: 2.4rem;
            }

            &:last-of-type {
              margin-left: 2.4rem;
            }
          }

          .vue-slider {
            flex-grow: 1;
            margin-right: 3.2rem;
          }
        }
      }
    }

    .overview {
      display: flex;
      flex-direction: row;
      align-items: center;
      margin-top: 5rem;
      background: #eee;
      padding: 1rem;

      .text {
        display: flex;
        flex-direction: column;

        p {
          margin-bottom: 0;

          &:first-of-type {
            text-transform: uppercase;
            color: $lift-blue;
          }
        }
      }

      svg {
        height: 6rem;
        margin-right: 2rem;
      }
    }

    .uh-oh {
      p {
        display: flex;
        padding: 1rem;

        svg {
          height: 2rem;
          margin-right: 1rem;
        }
      }
    }
  }
</style>
